import { formatDurationLong } from 'components_new/organisms/internal/DataRuns/utils'
import { RerunCandidate } from 'redux/selectors/internal/DataReruns'

/** One selected customer as it will be ordered. */
export interface RerunRow {
  candidate: RerunCandidate
  jobNames: string[]
  priority: boolean
}

interface RerunEstimate {
  minutes: number | null
  costUsd: number | null
}

/** Priority customers first, then the rest, both by name. */
export function orderByPriority<T>(
  items: T[],
  nameOf: (item: T) => string,
  isPriority: (item: T) => boolean
): T[] {
  return [...items].sort((a, b) => {
    if (isPriority(a) !== isPriority(b)) {
      return isPriority(a) ? -1 : 1
    }

    return nameOf(a).localeCompare(nameOf(b), 'sv')
  })
}

export function sumOrNull(values: (number | null)[]): number | null {
  const known = values.filter((value): value is number => value !== null)

  return known.length === 0
    ? null
    : known.reduce((sum, value) => sum + value, 0)
}

/** The SIM job plus the selected collection jobs; null when none has one. */
export function estimateRow(
  candidate: RerunCandidate,
  jobNames: string[]
): RerunEstimate {
  const jobs = [
    candidate.sim_job,
    ...candidate.collection_jobs.filter((job) =>
      jobNames.includes(job.job_name)
    )
  ]

  return {
    minutes: sumOrNull(jobs.map((job) => job.estimated_minutes)),
    costUsd: sumOrNull(jobs.map((job) => job.estimated_cost_usd))
  }
}

export function formatEstimateMinutes(minutes: number | null): string {
  return minutes === null ? '–' : `ca ${formatDurationLong(minutes)}`
}

/**
 * "3 insamlingsjobb → SIM + 5 jobb", "1 av 3 insamlingsjobb → SIM + 5 jobb"
 * or "Inga insamlingsjobb · SIM + 5 jobb".
 */
export function formatJobScope(
  selected: number,
  total: number,
  downstream: number
): string {
  const sim = downstream > 0 ? `SIM + ${downstream} jobb` : 'SIM'

  if (total === 0) {
    return `Inga insamlingsjobb · ${sim}`
  }

  return selected === total
    ? `${total} insamlingsjobb → ${sim}`
    : `${selected} av ${total} insamlingsjobb → ${sim}`
}

/** Collection jobs, the SIM job and the jobs the SIM starts, per customer. */
export function countJobs(rows: RerunRow[]): number {
  return rows.reduce(
    (sum, row) =>
      sum + row.jobNames.length + 1 + row.candidate.downstream_jobs.length,
    0
  )
}
